import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";
const VENDOR_API_BASE = '/api/vendor';

// Create axios instance with authentication
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const user = localStorage.getItem('user');
    let token = '';
    if (user) {
      try {
        token = JSON.parse(user).token;
      } catch (e) { }
    }
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const vendorService = {
  // Get all vendor applications with pagination, search, and filters
  getAllApplications: async (params = {}) => {
    const {
      page = 1,
      limit = 10,
      search = '',
      status = '',
      category = ''
    } = params;

    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    if (status) queryParams.append('status', status);
    if (category) queryParams.append('category', category);

    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/applications?${queryParams.toString()}`
    );
    return response.data;
  },

  // Get a specific vendor application by ID
  getApplicationById: async (applicationId) => {
    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/applications/${applicationId}`
    );
    return response.data;
  },

  // Approve a vendor application
  approveApplication: async (vendorId, approvalData = {}) => {
    const response = await axiosInstance.post(
      `${VENDOR_API_BASE}/approve`,
      {
        vendorId,
        ...approvalData
      }
    );
    return response.data;
  },

  // Reject a vendor application
  rejectApplication: async (vendorId, rejectionData = {}) => {
    const response = await axiosInstance.post(
      `${VENDOR_API_BASE}/reject`,
      {
        vendorId,
        ...rejectionData
      }
    );
    return response.data;
  },

  // Get vendor statistics and analytics
  getVendorStats: async () => {
    const response = await axiosInstance.get(`${VENDOR_API_BASE}/stats`);
    return response.data;
  },

  // Get vendors by category
  getVendorsByCategory: async (category) => {
    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/category/${category}`
    );
    return response.data;
  },

  // Get vendors by status
  getVendorsByStatus: async (status) => {
    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/status/${status}`
    );
    return response.data;
  },

  // Export vendor applications (for future CSV/Excel export functionality)
  exportApplications: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/applications/export?${queryParams.toString()}`,
      {
        responseType: 'blob'
      }
    );
    return response.data;
  },

  // Bulk operations (for future functionality)
  bulkApprove: async (vendorIds, approvalData = {}) => {
    const response = await axiosInstance.post(
      `${VENDOR_API_BASE}/bulk-approve`,
      {
        vendorIds,
        ...approvalData
      }
    );
    return response.data;
  },

  bulkReject: async (vendorIds, rejectionData = {}) => {
    const response = await axiosInstance.post(
      `${VENDOR_API_BASE}/bulk-reject`,
      {
        vendorIds,
        ...rejectionData
      }
    );
    return response.data;
  },

  // Get vendor application history
  getApplicationHistory: async (vendorId) => {
    const response = await axiosInstance.get(
      `${VENDOR_API_BASE}/applications/${vendorId}/history`
    );
    return response.data;
  },

  // Update vendor application status
  updateApplicationStatus: async (vendorId, status, reason = '') => {
    const response = await axiosInstance.patch(
      `${VENDOR_API_BASE}/applications/${vendorId}/status`,
      {
        status,
        reason
      }
    );
    return response.data;
  }
};

// Error handling wrapper
export const handleVendorApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    switch (status) {
      case 400:
        return `Bad Request: ${data.message || 'Invalid data provided'}`;
      case 401:
        return 'Unauthorized: Please log in again';
      case 403:
        return 'Forbidden: You do not have permission to perform this action';
      case 404:
        return 'Not Found: The requested resource was not found';
      case 409:
        return `Conflict: ${data.message || 'Resource already exists'}`;
      case 422:
        return `Validation Error: ${data.message || 'Invalid input data'}`;
      case 429:
        return 'Too Many Requests: Please try again later';
      case 500:
        return 'Internal Server Error: Please try again later';
      default:
        return `Error ${status}: ${data.message || 'Something went wrong'}`;
    }
  } else if (error.request) {
    // Request was made but no response received
    return 'Network Error: Please check your internet connection';
  } else {
    // Something else happened
    return `Error: ${error.message || 'Something went wrong'}`;
  }
};

// Response data transformer
export const transformVendorData = (data) => {
  if (!data) return null;

  return {
    ...data,
    // Format dates
    appliedDate: data.appliedDate ? new Date(data.appliedDate).toLocaleDateString() : 'N/A',
    approvalDate: data.approvalDate ? new Date(data.approvalDate).toLocaleDateString() : 'N/A',
    rejectedAt: data.rejectedAt ? new Date(data.rejectedAt).toLocaleDateString() : 'N/A',

    // Format status for display
    statusDisplay: data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1) : 'Unknown',

    // Format business category
    categoryDisplay: data.businessCategory ? data.businessCategory.replace(/([A-Z])/g, ' $1').trim() : 'N/A',

    // Format contact information
    contactDisplay: data.contactPhone ? `${data.contactPhone}${data.contactEmail ? ` | ${data.contactEmail}` : ''}` : 'N/A'
  };
};

// Pagination helper
export const createPaginationParams = (page, limit, filters = {}) => {
  return {
    page: Math.max(1, parseInt(page) || 1),
    limit: Math.min(100, Math.max(1, parseInt(limit) || 10)),
    ...filters
  };
};

// Search and filter helper
export const createSearchFilters = (searchTerm, statusFilter, categoryFilter) => {
  const filters = {};

  if (searchTerm) {
    filters.search = searchTerm.trim();
  }

  if (statusFilter && statusFilter !== 'all') {
    filters.status = statusFilter;
  }

  if (categoryFilter && categoryFilter !== 'all') {
    filters.category = categoryFilter;
  }

  return filters;
}; 